import { Loader2 } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { AppShell } from '@/components/layout/AppShell';
import { useListWorkflows } from '@/features/workflows/api/useListWorkflows';
import { toast } from '@/lib/notify';

import { useGetCustomization } from '../api/useGetCustomization';
import { useSaveCustomization } from '../api/useSaveCustomization';
import {
  useGetWorkflowCustomization,
  useSaveWorkflowCustomization,
} from '../api/useWorkflowCustomization';
import { BrandPreview } from '../components/BrandPreview';
import { CustomizationFields, Section } from '../components/CustomizationFields';
import { OrganizationLogoField } from '../components/OrganizationLogoField';
import {
  type CustomizationSettings,
  DEFAULT_CUSTOMIZATION,
  parseCustomization,
  serializeCustomization,
} from '../schemas/customizationSchemas';

// /customization — brand the applicant journey. Left column: controls; right
// column: a live mobile/desktop preview. Colours/typography/radii persist to
// the tenant (customization_json); the logo uploads to object storage (only its
// key/URL is stored — never base64). The preview reflects the saved state live.
export function CustomizationScreen() {
  const { data: orgData, isLoading } = useGetCustomization();
  const save = useSaveCustomization();
  const saveWorkflow = useSaveWorkflowCustomization();

  // Scope: 'default' = organization default; otherwise a workflow id (override).
  const [scope, setScope] = useState<string>('default');
  const isWorkflowScope = scope !== 'default';

  // Workflows for the scope dropdown (first 100 is plenty for the selector).
  const workflowsQuery = useListWorkflows({ page: 1, pageSize: 100 });
  const workflows = workflowsQuery.data?.items ?? [];

  // Selected workflow's override (only fetched in workflow scope).
  const wfQuery = useGetWorkflowCustomization(isWorkflowScope ? scope : null);

  // Editable working copy, seeded from whichever scope is active.
  const [settings, setSettings] = useState<CustomizationSettings>(DEFAULT_CUSTOMIZATION);
  const [dirty, setDirty] = useState(false);

  // Logo is org-level only (per-workflow logo is a later step). Server resource
  // (presigned URL) — no client-side base64 preview.
  const logoUrl = orgData?.logoUrl ?? null;
  const isSaving = save.isPending || saveWorkflow.isPending;

  // Seed / re-seed the working copy when the active scope's data changes and the
  // user hasn't started editing. Default scope → org blob; workflow scope → the
  // workflow's override, falling back to the org default as the starting point.
  useEffect(() => {
    if (dirty) return;
    if (!isWorkflowScope) {
      if (orgData) setSettings(parseCustomization(orgData.customizationJson));
    } else if (wfQuery.data) {
      const base = wfQuery.data.customizationJson ?? orgData?.customizationJson ?? null;
      setSettings(parseCustomization(base));
    }
  }, [dirty, isWorkflowScope, orgData, wfQuery.data]);

  // Patch one nested group (fontColor / inputFieldColor / …) and mark dirty.
  function patch<K extends keyof CustomizationSettings>(
    key: K,
    value: Partial<CustomizationSettings[K]> | CustomizationSettings[K],
  ) {
    setSettings((prev) => {
      const current = prev[key];
      const next =
        typeof current === 'object' && current !== null
          ? { ...current, ...(value as object) }
          : (value as CustomizationSettings[K]);
      return { ...prev, [key]: next };
    });
    setDirty(true);
  }

  // Switching scope discards unsaved edits and re-seeds from the new source.
  function changeScope(next: string) {
    setScope(next);
    setDirty(false);
  }

  // Default scope: stage the built-in defaults (Save persists them as the org
  // default). Workflow scope: clear the override immediately → inherit org.
  function handleReset() {
    if (!isWorkflowScope) {
      setSettings(structuredClone(DEFAULT_CUSTOMIZATION));
      setDirty(true);
      return;
    }
    saveWorkflow.mutate(
      { workflowId: scope, customizationJson: null },
      {
        onSuccess: () => {
          setDirty(false);
          toast.success('Reverted to the organization default.');
        },
        onError: () => toast.error('Could not reset. Please try again.'),
      },
    );
  }

  function handleSave() {
    const customizationJson = serializeCustomization(settings);
    const onSuccess = () => {
      setDirty(false);
      toast.success('Branding saved.');
    };
    const onError = () => toast.error('Could not save branding. Please try again.');

    if (!isWorkflowScope) {
      save.mutate({ customizationJson }, { onSuccess, onError });
    } else {
      saveWorkflow.mutate({ workflowId: scope, customizationJson }, { onSuccess, onError });
    }
  }

  const preview = useMemo(() => settings, [settings]);

  if (isLoading) {
    return (
      <AppShell title="Customization" noPadding>
        <div className="flex h-full items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell title="Customization" noPadding>
      {/* noPadding locks the content area to the viewport (like the workflow
          editor), so the LEFT card scrolls internally and the RIGHT preview
          stays fixed — the page itself never scrolls on desktop. We re-add the
          page padding here. */}
      <div className="flex min-h-0 flex-1 flex-col gap-4 p-4 max-lg:overflow-y-auto lg:grid lg:grid-cols-2 lg:grid-rows-[minmax(0,1fr)] lg:gap-6 lg:overflow-hidden">
        {/* LEFT: controls */}
        <div className="flex flex-col gap-5 lg:h-full lg:min-h-0 lg:overflow-hidden lg:pr-1">
          <div>
            <h2 className="m-0 mb-1 text-lg font-semibold text-black-logo">Branding</h2>
            <p className="m-0 text-sm text-gray66">
              Customize your brand colors and preview changes in real time to create a consistent
              verification experience.
            </p>
          </div>

          <div className="flex min-h-0 flex-1 flex-col overflow-y-auto rounded-lg border border-grayEF p-3.5 custom-scrollbar">
            {/* Scope selector — organization default, or a per-workflow override. */}
            <div className="mb-4 flex items-center gap-6">
              <label htmlFor="cz-workflow" className="text-lg font-semibold text-black-logo">
                Select Your Workflow
              </label>
              <select
                id="cz-workflow"
                className="sc-select h-10 max-w-[325px] flex-1 text-sm"
                value={scope}
                onChange={(e) => changeScope(e.target.value)}
              >
                <option value="default">Default (Organization)</option>
                {workflows.map((w) => (
                  <option key={w.id} value={w.id}>
                    {w.name}
                  </option>
                ))}
              </select>
            </div>

            {isWorkflowScope && (
              <p className="-mt-2 mb-4 text-xs text-gray66">
                Editing this workflow&apos;s override. Colours here apply only to this workflow; the
                logo stays at the organization level. Use <b>Reset</b> to inherit the org default
                again.
              </p>
            )}

            {/* Logo — organization-level only (hidden in per-workflow scope). */}
            {!isWorkflowScope && (
              <Section
                title="Logo"
                subtitle="Add icon and full logos to maintain consistent branding across all screens."
              >
                <OrganizationLogoField logoUrl={logoUrl} />
              </Section>
            )}

            {/* Shared branding form (also used by the workflow editor drawer). */}
            <CustomizationFields settings={settings} onPatch={patch} />
          </div>

          {/* Footer actions */}
          <div className="flex items-center justify-center gap-3">
            <button
              type="button"
              onClick={handleReset}
              disabled={isSaving}
              className="btn btn-outlined px-7"
            >
              {isWorkflowScope ? 'Reset to org default' : 'Reset'}
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={isSaving || !dirty}
              className="btn btn-primary flex items-center gap-2 px-7"
            >
              {isSaving && <Loader2 size={15} className="animate-spin" />}
              Save
            </button>
          </div>
        </div>

        {/* RIGHT: live preview */}
        <div className="flex min-h-[420px] flex-col overflow-hidden lg:h-full lg:min-h-0">
          <BrandPreview settings={preview} logoUrl={logoUrl} />
        </div>
      </div>
    </AppShell>
  );
}
